import * as THREE from 'three';

const LINE_COLOR = 0x999999;
const LINE_WIDTH = 1;
const DASHED_LINE_SIZE = 0.02;
const DASHED_LINE_GAP = 0.015;
const DOTTED_LINE_SIZE = 0.005;
const DOTTED_LINE_GAP = 0.005;
const FLAT_MESH_COLOR = 0x333333;

const LINE_MATERIAL = new THREE.LineBasicMaterial({
  color: LINE_COLOR,
  linewidth: LINE_WIDTH,
});
const DASHED_LINE_MATERIAL = new THREE.LineDashedMaterial({
  color: LINE_COLOR,
  linewidth: LINE_WIDTH,
  dashSize: DASHED_LINE_SIZE,
  gapSize: DASHED_LINE_GAP,
});
const DOTTED_LINE_MATERIAL = new THREE.LineDashedMaterial({
  color: LINE_COLOR,
  linewidth: LINE_WIDTH,
  dashSize: DOTTED_LINE_SIZE,
  gapSize: DOTTED_LINE_GAP,
});
const FLAT_MESH_MATERIAL = new THREE.MeshBasicMaterial({
  color: FLAT_MESH_COLOR,
  side: THREE.DoubleSide,
});

export function createLeftHungOutwardOpeningMarker(width, height) {
  const geometry = new THREE.Geometry();
  geometry.vertices.push(
    new THREE.Vector3(-width / 2, -height / 2, 0),
    new THREE.Vector3(width / 2, 0, 0),
    new THREE.Vector3(-width / 2, height / 2, 0)
  );
  const line = new THREE.Line(geometry, LINE_MATERIAL);
  return line;
}

export function createRightHungOutwardOpeningMarker(width, height) {
  const geometry = new THREE.Geometry();
  geometry.vertices.push(
    new THREE.Vector3(width / 2, -height / 2, 0),
    new THREE.Vector3(-width / 2, 0, 0),
    new THREE.Vector3(width / 2, height / 2, 0)
  );
  const line = new THREE.Line(geometry, LINE_MATERIAL);
  return line;
}

export function createLeftHungInwardOpeningMarker(width, height) {
  const geometry = new THREE.Geometry();
  geometry.vertices.push(
    new THREE.Vector3(width / 2, -height / 2, 0),
    new THREE.Vector3(-width / 2, 0, 0),
    new THREE.Vector3(width / 2, height / 2, 0)
  );
  const line = new THREE.Line(geometry, DASHED_LINE_MATERIAL);
  line.computeLineDistances();
  return line;
}

export function createRightHungInwardOpeningMarker(width, height) {
  const geometry = new THREE.Geometry();
  geometry.vertices.push(
    new THREE.Vector3(-width / 2, -height / 2, 0),
    new THREE.Vector3(width / 2, 0, 0),
    new THREE.Vector3(-width / 2, height / 2, 0)
  );
  const line = new THREE.Line(geometry, DASHED_LINE_MATERIAL);
  line.computeLineDistances();
  return line;
}

export function createLeftHungKippDrehInwardOpeningMarker(width, height) {
  const geometry = new THREE.Geometry();
  geometry.vertices.push(
    new THREE.Vector3(width / 2, height / 2, 0),
    new THREE.Vector3(-width / 2, 0, 0),
    new THREE.Vector3(width / 2, -height / 2, 0),
    new THREE.Vector3(0, height / 2, 0),
    new THREE.Vector3(-width / 2, -height / 2, 0)
  );
  const line = new THREE.Line(geometry, DASHED_LINE_MATERIAL);
  line.computeLineDistances();
  return line;
}

export function createRightHungKippDrehInwardOpeningMarker(width, height) {
  const geometry = new THREE.Geometry();
  geometry.vertices.push(
    new THREE.Vector3(-width / 2, height / 2, 0),
    new THREE.Vector3(width / 2, 0, 0),
    new THREE.Vector3(-width / 2, -height / 2, 0),
    new THREE.Vector3(0, height / 2, 0),
    new THREE.Vector3(width / 2, -height / 2, 0)
  );
  const line = new THREE.Line(geometry, DASHED_LINE_MATERIAL);
  line.computeLineDistances();
  return line;
}

export function createTwistWindowOpeningMarker(width, height) {
  const upperLine = new THREE.Line(
    new THREE.Geometry().setFromPoints([
      new THREE.Vector3(-width / 2, -height / 4, 0),
      new THREE.Vector3(0, height / 2, 0),
      new THREE.Vector3(width / 2, -height / 4, 0),
    ]),
    LINE_MATERIAL
  );
  const lowerLine = new THREE.Line(
    new THREE.Geometry().setFromPoints([
      new THREE.Vector3(-width / 2, height / 4, 0),
      new THREE.Vector3(0, -height / 2, 0),
      new THREE.Vector3(width / 2, height / 4, 0),
    ]),
    LINE_MATERIAL
  );
  const upperDottedLine = new THREE.Line(
    new THREE.Geometry().setFromPoints([
      new THREE.Vector3(-width / 2, height / 4, 0),
      new THREE.Vector3(width / 2, height / 4, 0),
    ]),
    DOTTED_LINE_MATERIAL
  );
  upperDottedLine.computeLineDistances();
  const lowerDottedLine = new THREE.Line(
    new THREE.Geometry().setFromPoints([
      new THREE.Vector3(-width / 2, -height / 4, 0),
      new THREE.Vector3(width / 2, -height / 4, 0),
    ]),
    DOTTED_LINE_MATERIAL
  );
  lowerDottedLine.computeLineDistances();

  const object = new THREE.Object3D();
  object.add(upperLine);
  object.add(lowerLine);
  object.add(upperDottedLine);
  object.add(lowerDottedLine);
  return object;
}

export function createFixedFrameOpeningMarker(width, height) {
  const axisLength = Math.min(width, height) * 0.2;

  const topLeftLine = new THREE.Line(
    new THREE.Geometry().setFromPoints([
      new THREE.Vector3(-width / 2, height / 2, 0),
      new THREE.Vector3(-width / 2 + axisLength, height / 2 - axisLength, 0),
    ]),
    LINE_MATERIAL
  );
  const topRightLine = new THREE.Line(
    new THREE.Geometry().setFromPoints([
      new THREE.Vector3(width / 2, height / 2, 0),
      new THREE.Vector3(width / 2 - axisLength, height / 2 - axisLength, 0),
    ]),
    LINE_MATERIAL
  );
  const bottomLeftLine = new THREE.Line(
    new THREE.Geometry().setFromPoints([
      new THREE.Vector3(-width / 2, -height / 2, 0),
      new THREE.Vector3(-width / 2 + axisLength, -height / 2 + axisLength, 0),
    ]),
    LINE_MATERIAL
  );
  const bottomRightLine = new THREE.Line(
    new THREE.Geometry().setFromPoints([
      new THREE.Vector3(width / 2, -height / 2, 0),
      new THREE.Vector3(width / 2 - axisLength, -height / 2 + axisLength, 0),
    ]),
    LINE_MATERIAL
  );

  const object = new THREE.Object3D();
  object.add(topLeftLine);
  object.add(topRightLine);
  object.add(bottomLeftLine);
  object.add(bottomRightLine);
  return object;
}

export function createTopHungOutwardOpeningMarker(width, height) {
  const geometry = new THREE.Geometry().setFromPoints([
    new THREE.Vector3(-width / 2, height / 2, 0),
    new THREE.Vector3(0, -height / 2, 0),
    new THREE.Vector3(width / 2, height / 2, 0),
  ]);
  const line = new THREE.Line(geometry, LINE_MATERIAL);
  return line;
}

export function createBottomHungInwardOpeningMarker(width, height) {
  const geometry = new THREE.Geometry().setFromPoints([
    new THREE.Vector3(-width / 2, -height / 2, 0),
    new THREE.Vector3(0, height / 2, 0),
    new THREE.Vector3(width / 2, -height / 2, 0),
  ]);
  const line = new THREE.Line(geometry, DASHED_LINE_MATERIAL);
  line.computeLineDistances();
  return line;
}

export function createPushLeftOpeningMarker(width, height) {
  const lt = 0.015; // Line thickness
  const hh = 0.05; // Head height
  const hw = 0.05; // Head width
  const geometry = new THREE.Geometry();
  geometry.vertices.push(
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(hw, hh / 2, 0),
    new THREE.Vector3(hw, lt / 2, 0),
    new THREE.Vector3(width / 2, lt / 2, 0),
    new THREE.Vector3(width / 2, -lt / 2, 0),
    new THREE.Vector3(hw, -lt / 2, 0),
    new THREE.Vector3(hw, -hh / 2, 0)
  );
  geometry.faces.push(
    new THREE.Face3(0, 1, 2),
    new THREE.Face3(0, 2, 3),
    new THREE.Face3(0, 3, 4),
    new THREE.Face3(0, 4, 5),
    new THREE.Face3(0, 5, 6)
  );
  const mesh = new THREE.Mesh(geometry, FLAT_MESH_MATERIAL);
  return mesh;
}

export function createPushRightOpeningMarker(width, height) {
  const mesh = createPushLeftOpeningMarker(width, height);
  mesh.rotateY(Math.PI);
  return mesh;
}
